import React from 'react';
import { ChefHat, Clock, AlertCircle, CheckCircle, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { useOrders } from '../contexts/OrderContext';
import { cleanProductName, displayProductName } from '../utils/formatters';
import { filterTodayOrders } from '../utils/dateFilters';
import { useDocumentTitle } from '../utils/useDocumentTitle';
export function ProductionView() {
  useDocumentTitle('Producción');
  const {
    orders
  } = useOrders();
  // Filter orders: only today's orders, exclude cancelled
  const todayOrders = filterTodayOrders(orders).filter(o => o.status !== 'Cancelado');
  // Calculate production stats from TODAY'S orders only
  const pendingOrders = todayOrders.filter(o => o.status === 'Pendiente' || o.status === 'En Preparación');
  const completedOrders = todayOrders.filter(o => o.status === 'Listo' || o.status === 'Entregado');
  const totalPending = pendingOrders.length;
  const totalCompleted = completedOrders.length;
  const totalOrders = todayOrders.length;
  // Group by product for production queue (clean product names)
  const productionQueue = pendingOrders.reduce((acc, order) => {
    const cleanName = displayProductName(order.product);
    if (!acc[cleanName]) {
      acc[cleanName] = {
        name: cleanName,
        count: 0,
        priority: 'medium'
      };
    }
    acc[cleanName].count += order.quantity;
    return acc;
  }, {} as Record<string, {
    name: string;
    count: number;
    priority: string;
  }>);
  const queueItems = Object.values(productionQueue).sort((a, b) => b.count - a.count);
  return <div className="p-8 max-w-[1600px] mx-auto" data-id="element-5129">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-5130">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-id="element-5131">
          <div className="flex items-center justify-between mb-4" data-id="element-5132">
            <div className="p-3 bg-gray-100 rounded-xl" data-id="element-5133">
              <ChefHat className="w-6 h-6 text-gray-900" data-id="element-5134" />
            </div>
            <TrendingUp className="w-5 h-5 text-gray-400" data-id="element-5135" />
          </div>
          <p className="text-sm text-gray-500 font-medium mb-1" data-id="element-5136">Total Hoy</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-5137">{totalOrders}</p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-id="element-5138">
          <div className="flex items-center justify-between mb-4" data-id="element-5139">
            <div className="p-3 bg-gray-100 rounded-xl" data-id="element-5140">
              <CheckCircle className="w-6 h-6 text-gray-900" data-id="element-5141" />
            </div>
            <span className="text-xs text-gray-500 font-medium" data-id="element-5142">
              {totalOrders > 0 ? Math.round(totalCompleted / totalOrders * 100) : 0}
              %
            </span>
          </div>
          <p className="text-sm text-gray-500 font-medium mb-1" data-id="element-5143">Completado</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-5144">{totalCompleted}</p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-id="element-5145">
          <div className="flex items-center justify-between mb-4" data-id="element-5146">
            <div className="p-3 bg-gray-100 rounded-xl" data-id="element-5147">
              <AlertCircle className="w-6 h-6 text-gray-900" data-id="element-5148" />
            </div>
            <Clock className="w-5 h-5 text-gray-400" data-id="element-5149" />
          </div>
          <p className="text-sm text-gray-500 font-medium mb-1" data-id="element-5150">Pendiente</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-5151">{totalPending}</p>
        </div>
      </div>

      {/* Production Queue */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200" data-id="element-5152">
        <div className="flex items-center justify-between mb-6" data-id="element-5153">
          <div data-id="element-5154">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-5155">
              Cola de Producción - Hoy
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-5156">
              Pedidos pendientes de preparación para hoy
            </p>
          </div>
          <div className="text-sm text-gray-500" data-id="element-5157">
            {pendingOrders.length} pedidos pendientes
          </div>
        </div>

        <div className="space-y-3" data-id="element-5158">
          {pendingOrders.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-5159">
              No hay órdenes pendientes para hoy.
            </div> : pendingOrders.map((order, index) => {
          // Parse product selections from the product field
          const productParts = order.product.split(' | ').map((p: string) => p.trim()).filter(Boolean);
          const mainProduct = displayProductName(productParts[0] || order.product);
          const selections = productParts.slice(1);
          return <div key={order.id} className="p-4 rounded-xl border border-gray-200 bg-gray-50" data-id="element-5160">
                  <div className="flex items-start justify-between gap-4" data-id="element-5161">
                    <div className="flex-1 min-w-0" data-id="element-5162">
                      <div className="flex items-center gap-3 mb-1" data-id="element-5163">
                        <span className="text-xs font-mono text-gray-400 bg-gray-200 px-2 py-0.5 rounded" data-id="element-5164">
                          #{order.order_no}
                        </span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${order.status === 'Pendiente' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-5165">
                          {order.status}
                        </span>
                      </div>
                      <h3 className="text-base font-bold text-gray-900" data-id="element-5166">
                        {mainProduct}
                      </h3>
                      {selections.length > 0 && <div className="mt-2 space-y-1" data-id="element-5167">
                          {selections.map((sel: string, i: number) => <div key={i} className="flex items-center gap-2 text-sm text-gray-600" data-id="element-5168">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-400 shrink-0" data-id="element-5169" />
                              {sel}
                            </div>)}
                        </div>}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500" data-id="element-5170">
                        <span data-id="element-5171">{order.employee_name}</span>
                        <span data-id="element-5172">•</span>
                        <span data-id="element-5173">{order.company}</span>
                        {order.branch && <>
                            <span data-id="element-5174">•</span>
                            <span data-id="element-5175">{order.branch}</span>
                          </>}
                      </div>
                    </div>
                    <div className="text-right shrink-0" data-id="element-5176">
                      <p className="text-xs text-gray-500 mb-1" data-id="element-5177">Cant.</p>
                      <p className="text-2xl font-bold text-gray-900" data-id="element-5178">
                        {order.quantity}
                      </p>
                    </div>
                  </div>
                </div>;
        })}
        </div>
      </div>
    </div>;
}